import React, { useState, useEffect } from 'react';
import type { DiscoveryGuideProfile } from '@common/domain/discovery-guide';
import { ChatUiMessage } from '../../lib/interfaces/Chat';
import { SCOUT_GUIDE_PROFILE } from '../../lib/discovery/scoutGuide';
import type { GoalChecklistSnapshot } from './goal-checklist/goalChecklist';

export {
    SCOUT_AVATAR_IMAGE_URL,
    SCOUT_GUIDE_PROFILE,
    SCOUT_PERSONA,
    SCOUT_PERSONA_ID,
    includeScoutPersona,
    isScoutPersona,
    resolveScoutGuideProfile,
} from '../../lib/discovery/scoutGuide';

// ================================================================
// Scout discovery mode — shared by TextChatPage and ChatBubble
// ================================================================

// Mirror of the criterion ids in backend/src/goals/discovery-goal.ts
// (user_discovery_v1). Keep in sync when the spec's criteria change.
const SCOUT_CONTEXT_ITEMS = [
    { id: 'communication_preferences', label: 'Communication' },
    { id: 'current_goals_projects', label: 'Goals/projects' },
    { id: 'life_work_context', label: 'Life/work context' },
    { id: 'constraints_boundaries', label: 'Boundaries', optional: true },
    { id: 'preferred_support_style', label: 'Support style' },
    { id: 'planning_style', label: 'Planning style', optional: true },
    { id: 'important_relationships', label: 'Relationships', optional: true },
];

export interface ScoutGoalObservability {
    status?: string;
    progressPercentage?: number;
    satisfiedCriteria?: string[];
    partialCriteria?: string[];
    missingCriteria?: string[];
    blockedCriteria?: string[];
    deferredCriteria?: string[];
    currentCriterionId?: string | null;
    nextPlannedMove?: string;
}

export interface ScoutGoalMetadata {
    decision?: string;
    runtime?: {
        observability?: ScoutGoalObservability | null;
        libraryGoalName?: string;
        skipped?: boolean;
        reason?: string;
    } | null;
}

export interface ScopedScoutStatusGoal {
    scopeKey: string;
    goal: ScoutGoalMetadata | null;
}

export function scoutGoalScopeKey(
    sessionId: string | null | undefined,
    personaId: string | null | undefined,
    testRunId: string | null | undefined,
): string {
    return JSON.stringify([sessionId ?? null, personaId ?? null, testRunId ?? null]);
}

export function selectScopedScoutStatusGoal(
    statusGoal: ScopedScoutStatusGoal | null,
    scopeKey: string,
): ScoutGoalMetadata | null {
    return statusGoal?.scopeKey === scopeKey ? statusGoal.goal : null;
}

function scoutAsRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function scoutStringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

function toScoutGoalMetadata(value: unknown): ScoutGoalMetadata | null {
    const record = scoutAsRecord(value);
    if (!record) return null;
    const runtimeRecord = scoutAsRecord(record.runtime);
    const observabilityRecord = scoutAsRecord(runtimeRecord?.observability);

    return {
        decision: typeof record.decision === 'string' ? record.decision : undefined,
        runtime: runtimeRecord
            ? {
                libraryGoalName:
                    typeof runtimeRecord.libraryGoalName === 'string' ? runtimeRecord.libraryGoalName : undefined,
                skipped: typeof runtimeRecord.skipped === 'boolean' ? runtimeRecord.skipped : undefined,
                reason: typeof runtimeRecord.reason === 'string' ? runtimeRecord.reason : undefined,
                observability: observabilityRecord
                    ? {
                        status:
                            typeof observabilityRecord.status === 'string' ? observabilityRecord.status : undefined,
                        progressPercentage:
                            typeof observabilityRecord.progressPercentage === 'number'
                                ? observabilityRecord.progressPercentage
                                : undefined,
                        satisfiedCriteria: scoutStringArray(observabilityRecord.satisfiedCriteria),
                        partialCriteria: scoutStringArray(observabilityRecord.partialCriteria),
                        missingCriteria: scoutStringArray(observabilityRecord.missingCriteria),
                        blockedCriteria: scoutStringArray(observabilityRecord.blockedCriteria),
                        deferredCriteria: scoutStringArray(observabilityRecord.deferredCriteria),
                        currentCriterionId:
                            typeof observabilityRecord.currentCriterionId === 'string'
                                ? observabilityRecord.currentCriterionId
                                : null,
                        nextPlannedMove:
                            typeof observabilityRecord.nextPlannedMove === 'string'
                                ? observabilityRecord.nextPlannedMove
                                : undefined,
                    }
                    : null,
            }
            : null,
    };
}

export function latestScoutGoalMetadata(messages: ChatUiMessage[]): ScoutGoalMetadata | null {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const goal = toScoutGoalMetadata(messages[index]?.goal);
        if (goal) return goal;
    }
    return null;
}

const SCOUT_GOAL_STORAGE_PREFIX = 'scoutGoalSnapshot:';
const SCOUT_COMPLETE_FLAG_PREFIX = 'scoutDiscoveryComplete:';
export const SCOUT_EXTENDED_PREFIX = 'scoutExtended:';

function scoutScopedKey(prefix: string, id: string, scopeId?: string | null): string {
    return scopeId ? `${prefix}${id}:${scopeId}` : `${prefix}${id}`;
}

export function readPersistedScoutGoal(
    sessionId: string | null | undefined,
    scopeId?: string | null,
): ScoutGoalMetadata | null {
    if (!sessionId) return null;
    try {
        const raw = window.localStorage.getItem(scoutScopedKey(SCOUT_GOAL_STORAGE_PREFIX, sessionId, scopeId));
        return raw ? toScoutGoalMetadata(JSON.parse(raw)) : null;
    } catch {
        return null;
    }
}

export function persistScoutGoal(
    sessionId: string | null | undefined,
    goal: ScoutGoalMetadata,
    scopeId?: string | null,
): void {
    if (!sessionId) return;
    try {
        window.localStorage.setItem(scoutScopedKey(SCOUT_GOAL_STORAGE_PREFIX, sessionId, scopeId), JSON.stringify(goal));
    } catch {
        // Storage unavailable/full: the panel simply will not survive reloads.
    }
}

function scoutUserKey(userId: string | undefined, scopeId?: string | null): string {
    return scoutScopedKey(SCOUT_COMPLETE_FLAG_PREFIX, userId || 'anonymous', scopeId);
}

export function readScoutDiscoveryComplete(userId: string | undefined, scopeId?: string | null): boolean {
    try {
        return window.localStorage.getItem(scoutUserKey(userId, scopeId)) === '1';
    } catch {
        return false;
    }
}

export function markScoutDiscoveryComplete(userId: string | undefined, scopeId?: string | null): void {
    try {
        window.localStorage.setItem(scoutUserKey(userId, scopeId), '1');
    } catch {
        // Best effort: the returning-user greeting still comes from the backend.
    }
}

function scoutItemStatus(
    markerId: string,
    observability: ScoutGoalObservability,
): GoalChecklistSnapshot['items'][number]['status'] {
    if (observability.satisfiedCriteria?.includes(markerId)) return 'completed';
    if (observability.currentCriterionId === markerId) return 'active';
    if (observability.partialCriteria?.includes(markerId)) return 'partial';
    if (observability.blockedCriteria?.includes(markerId)) return 'blocked';
    if (observability.deferredCriteria?.includes(markerId)) return 'deferred';
    return 'pending';
}

export function buildScoutGoalChecklistSnapshot(
    goal: ScoutGoalMetadata | null | undefined,
    revision: number,
): GoalChecklistSnapshot | null {
    const observability = goal?.runtime?.observability;
    if (!observability) return null;
    const items = SCOUT_CONTEXT_ITEMS.map((marker) => ({
        ...marker,
        status: scoutItemStatus(marker.id, observability),
    }));
    const completedCount = items.filter((item) => item.status === 'completed').length;
    const completed = observability.status === 'satisfied' || (observability.progressPercentage ?? 0) >= 100;

    return {
        goalId: 'user_discovery_v1',
        revision: Math.max(0, revision),
        title: goal.runtime?.libraryGoalName || 'User Discovery',
        status: completed ? 'completed' : goal.runtime?.skipped ? 'paused' : 'active',
        activeItemId: completed ? null : observability.currentCriterionId ?? null,
        completedCount,
        totalCount: items.length,
        updatedAt: new Date().toISOString(),
        items,
    };
}

export const ScoutPondering: React.FC<{ profile?: DiscoveryGuideProfile }> = ({
    profile = SCOUT_GUIDE_PROFILE,
}) => {
    const messages = profile.thinkingMessages.length > 0
        ? profile.thinkingMessages
        : SCOUT_GUIDE_PROFILE.thinkingMessages;
    const [index, setIndex] = useState(() => Math.floor(Math.random() * messages.length));

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setIndex((current) => (current + 1) % messages.length);
        }, 3500);
        return () => window.clearInterval(intervalId);
    }, [messages.length]);

    return (
        <span className="scout-pondering" key={index}>
            {messages[index % messages.length]}
        </span>
    );
};
